/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { List } from 'react-virtualized/dist/commonjs';

import Infotip from '../uiold/Infotip';
import { DataTable, Text } from '../uiold';

import { InputCell, InputHeaderCell } from './InputCell';
import stringCompare from '../../support/helpers/stringCompare';
import useComponentSize from '../../support/helpers/useComponentSize';
import { canUser } from '../../support/shared/permissionHelpers';

import { ListRowProps } from 'react-virtualized';
import {
  MarketPayments,
  VendorSalesByPayment,
  MarketNoteCategories,
  VendorNotesByCategory,
} from '../../types/shared/MarketSales';
import { VendorSummaryWithMarkets } from '../../types/shared/Vendor';
import { User } from '../../types/shared/User';
import { SortOrder } from '../uiold/DataTable/DataTableHeaderCell';

export type VendorWithPlacementAndSales = VendorSummaryWithMarkets & {
  placement: Array<string>;
  sales: VendorSalesByPayment;
  marketNotes: VendorNotesByCategory;
};

type SortableField = 'placement' | 'name' | 'contact' | 'category';

type Props = {
  vendors: Array<VendorWithPlacementAndSales>;
  marketPayments: MarketPayments;
  marketNoteCategories: MarketNoteCategories;
  user: User | null | undefined;
};

type HeaderProps = {
  onHeaderPress: (field: SortableField) => void;
  sortBy: SortableField;
  sortOrder: SortOrder;
  paymentNames: Array<string>;
  noteNames: Array<string>;
};

type RowProps = {
  vendor: VendorWithPlacementAndSales;
  paymentIDs: Array<string>;
  noteCategoryIDs: Array<string>;
  style: StyleSet;
  user: User | null | undefined;
};

type TotalRowProps = {
  style: StyleSet;
  paymentIDs: Array<string>;
  totalPaymentsByID: Map<string, number>;
};

function MarketSalesTable(props: Props) {
  let { vendors, marketPayments, marketNoteCategories } = props;
  let [sortBy, setSortBy] = useState('name' as SortableField);
  let [sortOrder, setSortOrder] = useState('asc' as SortOrder);
  let [tableSize, onLayout] = useComponentSize();

  let onHeaderPress = useCallback((field: SortableField) => {
    setSortBy((sortBy) => {
      if (sortBy === field) {
        // Toggle the sort order
        setSortOrder((sortOrder) => (sortOrder === 'asc' ? 'desc' : 'asc'));
      }
      return field;
    });
  }, []);

  let { paymentIDs, paymentNames, noteCategoryIDs, noteNames } = useMemo(
    () => ({
      paymentIDs: Object.keys(marketPayments),
      paymentNames: Object.values(marketPayments).map(
        (p) => (p as any).name as string,
      ),
      noteCategoryIDs: Object.keys(marketNoteCategories),
      noteNames: Object.values(marketNoteCategories).map(
        (p) => (p as any).name as string,
      ),
    }),
    [marketPayments, marketNoteCategories],
  );

  let sortedVendors = sortVendors(vendors, sortBy);

  if (sortOrder === 'desc') {
    sortedVendors.reverse();
  }

  let totalPaymentsByID = new Map();

  sortedVendors.forEach((vendor) => {
    for (let [paymentID, payment] of Object.entries(vendor.sales)) {
      let previousTotal = totalPaymentsByID.get(paymentID) || 0;
      totalPaymentsByID.set(
        paymentID, // Object.entries changed the type signature of the iterated value to mixed.
        previousTotal + ((payment as any) as number),
      );
    }
  });

  let rowRenderer = ({ key, index, style }: ListRowProps) => {
    let vendor = sortedVendors[index];
    let user = props.user;
    // Last row is reserved for the total.
    let isTotalRow = index === sortedVendors.length;

    if (isTotalRow) {
      return (
        <TotalPaymentRow
          key={key}
          paymentIDs={paymentIDs}
          totalPaymentsByID={totalPaymentsByID}
          style={style as any}
        />
      );
    }
    return (
      <MarketSalesRow
        key={key}
        vendor={
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          vendor!
        }
        paymentIDs={paymentIDs}
        noteCategoryIDs={noteCategoryIDs}
        style={style as any}
        user={user}
      />
    );
  };

  return (
    <Fragment>
      <TableHeader
        onHeaderPress={onHeaderPress}
        sortBy={sortBy}
        sortOrder={sortOrder}
        paymentNames={paymentNames}
        noteNames={noteNames}
      />
      <View style={styles.body} onLayout={onLayout}>
        {tableSize && (
          <List
            width={tableSize.width}
            height={tableSize.height} // Add one to include the total row
            rowCount={vendors.length + 1}
            rowHeight={DataTable.Row.totalHeight}
            rowRenderer={rowRenderer}
          />
        )}
      </View>
    </Fragment>
  );
}

function TableHeader({
  onHeaderPress,
  sortOrder,
  sortBy,
  paymentNames,
  noteNames,
}: HeaderProps) {
  return (
    <View style={styles.header}>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'placement' ? sortOrder : (null as any)}
        onPress={() => onHeaderPress('placement')}
        style={styles.placementHeader}
      >
        STALL
      </DataTable.HeaderCell>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'name' ? sortOrder : (null as any)}
        onPress={() => onHeaderPress('name')}
      >
        NAME
      </DataTable.HeaderCell>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'contact' ? sortOrder : (null as any)}
        onPress={() => onHeaderPress('contact')}
      >
        CONTACT PERSON
      </DataTable.HeaderCell>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'category' ? sortOrder : (null as any)}
        onPress={() => onHeaderPress('category')}
      >
        CATEGORY
      </DataTable.HeaderCell>
      {noteNames.map((n) => (
        <InputHeaderCell key={n}>{n}</InputHeaderCell>
      ))}
      {paymentNames.map((n) => (
        <InputHeaderCell key={n}>{n}</InputHeaderCell>
      ))}

      <DataTable.HeaderCell style={styles.totalColumn}>
        TOTAL
      </DataTable.HeaderCell>
    </View>
  );
}

function MarketSalesRow(props: RowProps) {
  let { vendor, paymentIDs, noteCategoryIDs, style, user } = props;
  let totalPayment = paymentIDs
    .map((id) => vendor.sales[id] || 0)
    .reduce((a, b) => a + b)
    .toFixed(2);
  let userCanWrite = user && canUser(user, { marketSales: 'WRITE' });
  return (
    <DataTable.Row virtualizedStyle={style}>
      <PlacementCell placement={vendor.placement} />
      <DataTable.Cell>{vendor.name}</DataTable.Cell>
      <DataTable.Cell>{vendor.contact}</DataTable.Cell>
      <DataTable.Cell>{(vendor.category || []).join(', ')}</DataTable.Cell>
      {noteCategoryIDs.map((id) => (
        <InputCell
          key={id}
          vendorID={vendor.id}
          noteCategoryID={id}
          initialValue={vendor.marketNotes[id] || ''}
          disabled={!userCanWrite}
        />
      ))}
      {paymentIDs.map((id) => (
        <InputCell
          key={id}
          vendorID={vendor.id}
          paymentID={id}
          initialValue={vendor.sales[id] || 0}
          disabled={!userCanWrite}
          allowNegative={true}
        />
      ))}

      {/* Pass the total payment as title so if the number overflows, the full
      value can be seen by hovering. */}
      <DataTable.Cell style={styles.totalColumn} title={String(totalPayment)}>
        {totalPayment}
      </DataTable.Cell>
    </DataTable.Row>
  );
}

function TotalPaymentRow(props: TotalRowProps) {
  let { totalPaymentsByID, paymentIDs, style } = props;
  let paymentTotals = paymentIDs.map((id) => totalPaymentsByID.get(id) || 0);
  let total = paymentTotals.reduce((a, b) => a + b, 0).toFixed(2);
  return (
    <DataTable.Row virtualizedStyle={style}>
      {/* <PlacementCell placement={vendor.placement} /> */}
      <DataTable.Cell style={styles.body}>TOTAL</DataTable.Cell>
      {/* <DataTable.Cell>{vendor.contact}</DataTable.Cell>
      <DataTable.Cell>{(vendor.category || []).join(', ')}</DataTable.Cell> */}
      {paymentTotals.map((t, i) => (
        <DataTable.Cell key={i} style={styles.totalCell}>
          {t}
        </DataTable.Cell>
      ))}
      {/* Pass the total payment as title so when the number overflow, the full value
      could be seen by hovering the visible part. */}
      <DataTable.Cell style={styles.totalColumn} title={String(total)}>
        {total}
      </DataTable.Cell>
    </DataTable.Row>
  );
}

function PlacementCell({ placement }: { placement: Array<string> }) {
  let [firstPlacement, ...otherPlacement] = placement;
  return (
    <View style={styles.placementColumn}>
      <Text type="label">{firstPlacement}</Text>
      {!!otherPlacement.length && (
        <View style={styles.infotip}>
          <Infotip text={otherPlacement.toString()} isShrinking oneLine />
        </View>
      )}
    </View>
  );
}

function sortVendors(
  vendors: Array<VendorWithPlacementAndSales>,
  sortBy: SortableField,
) {
  return vendors.sort((a, b) => {
    switch (sortBy) {
      case 'category':
        return stringCompare(
          (a.category || [])[0] || '',
          (b.category || [])[0] || '',
        );
      case 'placement':
        return stringCompare(a.placement[0] || '', b.placement[0] || '');
      default:
        return stringCompare(a[sortBy] ?? '', b[sortBy] ?? '');
    }
  });
}

let styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    paddingVertical: 5,
    alignItems: 'flex-start',
  },
  body: {
    flex: 1,
  },
  placementHeader: {
    maxWidth: 70,
    flexDirection: 'row',
    zIndex: 2,
  },
  placementColumn: {
    maxWidth: 80,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 40,
    zIndex: 2,
  },
  infotip: {
    position: 'absolute',
    left: 12,
  },
  totalColumn: {
    marginLeft: 5,
    justifyContent: 'flex-end',
    minWidth: 60,
    maxWidth: 80,
    textAlign: 'right',
  },
  totalCell: {
    alignItems: 'center',
    flexDirection: 'row',
    maxWidth: 80,
    textAlign: 'right',
    justifyContent: 'flex-end',
  },
});

export default MarketSalesTable;
